import { motion } from "framer-motion";
import { useSiteContent } from "@/context/SiteContent";
import DesignGallery from "@/components/landing/DesignGallery";

const EASE = [0.16, 1, 0.3, 1];

// Kreative Ausstellung — Gruppe 1: alle 5 Original-Handskizzen von Justin
// (Original-Dateien MIT eingebranntem "made by justin"-Wasserzeichen — Diebstahlschutz)
const CLOTHING_WORKS = [
  {
    src: "/images/designs/design-5.jpg",
    title: "Zip-Hoodie & Cargo — Signature Set",
    meta: "Originalskizze aus Justins Hand",
    span: "lg:col-span-7",
    dark: true,
  },
  {
    src: "/images/designs/design-2.jpg",
    title: "Cap · Jacke · Star Pants",
    meta: "Originalskizze aus Justins Hand",
    span: "lg:col-span-5",
    dark: true,
  },
  {
    src: "/images/designs/design-1.jpg",
    title: "Camo-Overshirt mit Layer-Ärmeln",
    meta: "Originalskizze aus Justins Hand",
    span: "lg:col-span-4",
    dark: true,
  },
  {
    src: "/images/designs/design-3.jpg",
    title: "Utility-Weste & Cargo-Shorts",
    meta: "Originalskizze aus Justins Hand",
    span: "lg:col-span-4",
    dark: true,
  },
  {
    src: "/images/designs/design-4.jpg",
    title: "Leo-Zipper & Double-Knee Cargo",
    meta: "Originalskizze aus Justins Hand",
    span: "lg:col-span-4",
    dark: true,
  },
];

// Gruppe 2: Webdesign-Arbeiten aus dem Studio
const WEB_WORKS = [
  {
    src: "/images/websites/premium-1.jpg",
    title: "Business-Dashboard",
    meta: "Webdesign — Admin-System",
    span: "lg:col-span-4",
  },
  {
    src: "/images/websites/plus-2.jpg",
    title: "Buchungssystem",
    meta: "Webdesign — Terminkalender",
    span: "lg:col-span-4",
  },
  {
    src: "/images/websites/standard-1.jpg",
    title: "Unternehmenswebsite",
    meta: "Webdesign",
    span: "lg:col-span-4",
  },
];

const WorkFigure = ({ w, i, testId }) => (
  <motion.figure
    initial={{ opacity: 0, y: 48, scale: 0.94, rotateX: 4 }}
    whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
    viewport={{ once: true, margin: "60px" }}
    transition={{ duration: 0.9, ease: EASE, delay: (i % 3) * 0.1 }}
    style={{ transformPerspective: 1000 }}
    data-testid={testId}
    className={`group sm:col-span-1 ${w.span}`}
  >
    <div className={`overflow-hidden border border-black/10 ${w.dark ? "bg-[#0a0a0a]" : "bg-black/5"}`}>
      <img
        src={w.src}
        alt={`${w.title} — ${w.meta}`}
        loading="lazy"
        className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
      />
    </div>
    <figcaption className="mt-3 flex items-baseline justify-between gap-3">
      <span className="font-display uppercase tracking-tight text-base">{w.title}</span>
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-right shrink-0">{w.meta}</span>
    </figcaption>
  </motion.figure>
);

export default function Portfolio() {
  const { content } = useSiteContent();
  // Content-Store (KI-Manager) kann die Portfolio-Liste komplett ersetzen;
  // leere Liste = Fallback auf Defaults, damit das Portfolio nie leer ausgeht
  const storeItems = Array.isArray(content.portfolio) && content.portfolio.length > 0 ? content.portfolio : null;
  const clothingWorks = storeItems ? storeItems.filter((w) => w.group === "clothing") : CLOTHING_WORKS;
  const webWorks = storeItems ? storeItems.filter((w) => w.group === "web") : WEB_WORKS;
  return (
    <section id="portfolio" data-testid="portfolio-section" className="px-6 lg:px-12 py-24 lg:py-36 max-w-[1600px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "100px" }}
        transition={{ duration: 0.75, ease: EASE }}
        className="mb-14 lg:mb-20 max-w-3xl"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-bold mb-6">Kapitel 04 — Portfolio</p>
        <h2 className="font-display uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-6xl">
          Echte Arbeit.{" "}
          <span className="font-editorial italic normal-case tracking-normal text-gold">Keine Templates.</span>
        </h2>
        <p className="mt-6 text-base lg:text-lg leading-relaxed text-zinc-500">
          Fünf Clothing-Designs aus meiner eigenen Hand und digitale Systeme aus meinem Studio —
          jedes Stück entstand von der ersten Linie bis zum fertigen Produkt hier.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5 lg:gap-7" data-testid="portfolio-grid">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "60px" }}
          transition={{ duration: 0.6, ease: EASE }}
          data-testid="portfolio-group-clothing"
          className="sm:col-span-2 lg:col-span-12 text-xs uppercase tracking-[0.3em] text-zinc-400 font-bold border-b border-black/10 pb-3"
        >
          Clothing Design — Meine Originalskizzen
        </motion.p>
        {/* Interaktive 3D-Fashion-Galerie: Drag/Swipe, kein Auto-Wechsel.
            Inhalte (Bilder, Titel, Reihenfolge) bleiben exakt die bestehenden. */}
        <DesignGallery items={clothingWorks} />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "60px" }}
          transition={{ duration: 0.6, ease: EASE }}
          data-testid="portfolio-group-webdesign"
          className="sm:col-span-2 lg:col-span-12 mt-10 lg:mt-14 text-xs uppercase tracking-[0.3em] text-zinc-400 font-bold border-b border-black/10 pb-3"
        >
          Webdesign — Aus meinem Studio
        </motion.p>
        {webWorks.map((w, i) => (
          <WorkFigure key={`${w.src}-${i}`} w={w} i={i + clothingWorks.length} testId={`portfolio-item-${i + clothingWorks.length}`} />
        ))}
      </div>

      <p className="mt-10 font-editorial italic text-lg text-zinc-400">
        Weitere Arbeiten folgen — dein Projekt könnte das nächste sein.
      </p>
    </section>
  );
}
